import { useEffect, useState, type ReactNode } from 'react';
import { oneliveAssets } from './assets';
import { fetchSiteBranding, navigate, resolveFooterCertUrl, type SiteFooterInfo } from './api';

interface Props {
  onNavigate?: () => void;
  className?: string;
  /** site = PC 하단(참고사이트 스타일), drawer = 모바일 햄버거 드로어 */
  variant?: 'site' | 'drawer';
}

function line(label: string, value?: string) {
  const text = (value ?? '').trim();
  if (!text) return null;
  return (
    <p>
      <span>{label}: {text}</span>
    </p>
  );
}

function Sep() {
  return <span className="onelive-footer-sep" aria-hidden="true">|</span>;
}

function SiteFooterBody({
  footer,
  onGo,
}: {
  footer: SiteFooterInfo | null;
  onGo: (path: string) => void;
}) {
  const legalName = (footer?.legal_name || footer?.company_name || '원라이브TV').trim();
  const companyName = (footer?.company_name || legalName).trim();

  const metaParts: ReactNode[] = [];
  const pushMeta = (label: string, value?: string) => {
    const text = (value ?? '').trim();
    if (!text) return;
    if (metaParts.length > 0) metaParts.push(<Sep key={`sep-${label}`} />);
    metaParts.push(
      <span key={label} className="onelive-footer-meta-item">
        {label} : {text}
      </span>,
    );
  };

  pushMeta('회사명', companyName);
  pushMeta('사업자등록번호', footer?.business_number);
  pushMeta('대표이사', footer?.representative);
  pushMeta('주소', footer?.address);

  return (
    <>
      <nav className="onelive-footer-links" aria-label="푸터 정책 링크">
        <button type="button" onClick={() => onGo('/page/terms')}>이용약관</button>
        <Sep />
        <button type="button" onClick={() => onGo('/page/privacy')}>개인정보처리방침</button>
        <Sep />
        <button type="button" onClick={() => onGo('/page/policy')}>운영정책</button>
        <Sep />
        <button type="button" onClick={() => onGo('/page/refund')}>청소년 보호 정책</button>
      </nav>

      {metaParts.length > 0 ? (
        <p className="onelive-footer-meta">{metaParts}</p>
      ) : null}

      <p className="onelive-footer-copy onelive-footer-copy--site">
        COPYRIGHT © {legalName}. ALL RIGHTS RESERVED.
      </p>
    </>
  );
}

function DrawerFooterBody({
  footer,
  certSrc,
  onCertError,
  onGo,
}: {
  footer: SiteFooterInfo | null;
  certSrc: string;
  onCertError: () => void;
  onGo: (path: string) => void;
}) {
  const year = footer?.copyright_year || '2019';
  const legalName = footer?.legal_name || '원라이브TV';

  return (
    <>
      <div className="onelive-footer-policy">
        <button type="button" onClick={() => onGo('/page/terms')}>이용약관</button>
        <button type="button" onClick={() => onGo('/page/privacy')}>개인정보처리방침</button>
        <button type="button" onClick={() => onGo('/page/refund')}>청소년보호정책</button>
      </div>

      <div className="onelive-footer-company">
        <div className="onelive-footer-info">
          {line('상호', footer?.company_name ? `(주)${footer.company_name}` : undefined)}
          {line('대표자', footer?.representative)}
          {line('주소', footer?.address)}
          {line('사업자번호', footer?.business_number)}
          {line('통신판매업 신고번호', footer?.commerce_number)}
          {line('개인정보보호 책임자', footer?.privacy_officer)}
          {line('관리자 이메일', footer?.admin_email)}
        </div>

        <div className="onelive-footer-cert">
          <img
            src={certSrc}
            alt="정보보호관리 체계인증서"
            width={72}
            height={72}
            onError={onCertError}
          />
          <span>정보보호관리<br />체계인증서</span>
        </div>
      </div>

      <p className="onelive-footer-copy">
        copyright © {year} <span>(주){legalName}.</span>
      </p>
    </>
  );
}

export default function OneliveFooterContent({
  onNavigate,
  className,
  variant = 'site',
}: Props) {
  const [footer, setFooter] = useState<SiteFooterInfo | null>(null);
  const [certSrc, setCertSrc] = useState(oneliveAssets.footerCert);

  useEffect(() => {
    let mounted = true;
    fetchSiteBranding().then((data) => {
      if (mounted && data?.footer) setFooter(data.footer);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setCertSrc(resolveFooterCertUrl(footer?.certificate_image));
  }, [footer]);

  const go = (path: string) => {
    onNavigate?.();
    navigate(path);
  };

  const rootClass = [
    'onelive-footer-content',
    variant === 'site' ? 'onelive-footer-content--site' : 'onelive-footer-content--drawer',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      {variant === 'drawer' ? (
        <DrawerFooterBody
          footer={footer}
          certSrc={certSrc}
          onCertError={() => setCertSrc(resolveFooterCertUrl())}
          onGo={go}
        />
      ) : (
        <SiteFooterBody
          footer={footer}
          onGo={go}
        />
      )}
    </div>
  );
}
